import React, { useState, useEffect } from "react";

interface BannerImageProps {
  imageName: string;
  alt: string;
  isActive: boolean;
  title?: string;
  subtitle?: string;
  titleSize?: 'normal' | 'small';
  titleAlign?: 'center' | 'left' | 'right';
  subtitleSize?: 'normal' | 'large';
  subtitleAlign?: 'center' | 'left' | 'right';
  containerAlign?: 'center' | 'left' | 'right';
  highlightWord?: { word: string; color: string };
  hasOverlay?: boolean;
}

const BannerImage: React.FC<BannerImageProps> = ({ 
  imageName, 
  alt, 
  isActive, 
  title, 
  subtitle,
  titleSize = 'normal',
  titleAlign = 'center',
  subtitleSize = 'normal',
  subtitleAlign = 'center',
  containerAlign = 'center',
  highlightWord,
  hasOverlay = false
}) => {
  const renderSubtitle = () => {
    if (!subtitle) return null;
    
    const upperSubtitle = subtitle.toUpperCase();
    const alignClass = subtitleAlign === 'right' ? 'banner-carousel-subtitle-right' : subtitleAlign === 'left' ? 'banner-carousel-subtitle-left' : '';
    const subtitleClass = `banner-carousel-subtitle ${subtitleSize === 'large' ? 'banner-carousel-subtitle-large' : ''} ${alignClass}`;
    
    if (highlightWord) {
      const upperWord = highlightWord.word.toUpperCase();
      const parts = upperSubtitle.split(upperWord);
      
      return (
        <p className={subtitleClass}>
          {parts[0]}
          <span style={{ color: highlightWord.color }}>{upperWord}</span>
          {parts[1]}
        </p>
      );
    }
    
    return <p className={subtitleClass}>{upperSubtitle}</p>;
  };

  return (
    <div className={`banner-carousel-item ${isActive ? 'active' : ''} ${hasOverlay ? 'has-overlay' : ''}`}>
      <picture>
        {/* WebP format for modern browsers - much lighter */}
        <source
          type="image/webp"
          srcSet={`
            img/${imageName}-small.webp 640w,
            img/${imageName}-medium.webp 1024w,
            img/${imageName}.webp 1920w
          `}
          sizes="100vw"
        />
        {/* JPG fallback with responsive sizes */}
        <source
          type="image/jpeg"
          srcSet={`
            img/${imageName}-small.jpg 640w,
            img/${imageName}-medium.jpg 1024w,
            img/${imageName}.jpg 1920w
          `}
          sizes="100vw"
        />
        {/* Fallback for browsers that don't support picture */}
        <img
          src={`img/${imageName}.jpg`}
          alt={alt}
          loading="eager"
        />
      </picture>
      {(title || subtitle) && (
        <div className={`banner-carousel-text-overlay ${containerAlign === 'left' ? 'banner-carousel-text-overlay-left' : containerAlign === 'right' ? 'banner-carousel-text-overlay-right' : ''}`}>
          {title && (
            <h1 className={`banner-carousel-title ${titleSize === 'small' ? 'banner-carousel-title-small' : ''} ${titleAlign === 'right' ? 'banner-carousel-title-right' : titleAlign === 'left' ? 'banner-carousel-title-left' : ''}`}>
              {title.toUpperCase()}
            </h1>
          )}
          {renderSubtitle()}
        </div>
      )}
    </div>
  );
};

interface Banner {
  name: string;
  alt: string;
  title?: string;
  subtitle?: string;
  titleSize?: 'normal' | 'small';
  titleAlign?: 'center' | 'left' | 'right';
  subtitleSize?: 'normal' | 'large';
  subtitleAlign?: 'center' | 'left' | 'right';
  containerAlign?: 'center' | 'left' | 'right';
  highlightWord?: { word: string; color: string };
  hasOverlay?: boolean;
}

const banners: Banner[] = [
  { 
    name: "banner1", 
    alt: "Banner 1",
    title: "Hola, esta es la comunidad",
    subtitle: "Que estabas buscando"
  },
  { 
    name: "banner2",
    alt: "Banner 2",
    title: "El dolor que te frena no es normal",
    subtitle: "Y acá tiene un espacio",
    subtitleAlign: 'right'
  },
  { 
    name: "banner3", 
    alt: "Banner 3",
    title: "Tu dolor no es invisible",
    subtitle: "Y acá nos sostenemos",
    titleSize: 'small',
    subtitleSize: 'large',
    highlightWord: { word: 'nos', color: '#b2cfef' },
    hasOverlay: true
  },
];

export const BannerSlider: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // Minimum swipe distance (in px) to trigger a slide change
  const minSwipeDistance = 50;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % banners.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [currentIndex]);

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? banners.length - 1 : prevIndex - 1
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % banners.length);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  return (
    <div 
      className="banner-carousel"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="banner-carousel-inner">
        {banners.map((banner, index) => (
          <BannerImage
            key={banner.name}
            imageName={banner.name}
            alt={banner.alt}
            isActive={index === currentIndex}
            title={banner.title}
            subtitle={banner.subtitle}
            titleSize={banner.titleSize}
            titleAlign={banner.titleAlign}
            subtitleSize={banner.subtitleSize}
            subtitleAlign={banner.subtitleAlign}
            containerAlign={banner.containerAlign}
            highlightWord={banner.highlightWord}
            hasOverlay={banner.hasOverlay}
          />
        ))}
      </div>

      <div className="banner-carousel-controls">
        <button
          className="banner-carousel-control prev"
          onClick={goToPrevious}
          aria-label="Previous slide"
        >
          ‹
        </button>
        <button
          className="banner-carousel-control next"
          onClick={goToNext}
          aria-label="Next slide"
        >
          ›
        </button>
      </div>

      <div className="banner-carousel-indicators">
        {banners.map((_, index) => (
          <button
            key={index}
            className={`banner-carousel-indicator ${index === currentIndex ? 'active' : ''}`}
            onClick={() => goToSlide(index)}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};
